import { motion } from "framer-motion";
import { Truck, Shield, RotateCcw, Clock, Gift, Users, Sparkles } from "lucide-react";

const badges = [
  {
    icon: Sparkles,
    title: "Handcrafted Excellence",
    description: "Meticulously crafted by skilled artisans with premium materials",
  },
  {
    icon: Shield,
    title: "Authentic Quality",
    description: "Genuine stones and certified authenticity with quality assurance",
  },
  {
    icon: Truck,
    title: "Free & Fast Delivery",
    description: "Free shipping above ₹500, express 24-hour delivery in Delhi NCR",
  },
  {
    icon: RotateCcw,
    title: "Easy Returns",
    description: "Hassle-free 7-day return policy with quick exchanges",
  },
  {
    icon: Gift,
    title: "Flexible Payment",
    description: "Multiple payment options including secure COD",
  },
  {
    icon: Users,
    title: "Trusted by Thousands",
    description: "Join 20,000+ happy customers who trust Aarsa",
  },
];

export default function TrustBadges() {
  return (
    <section id="why-aarsa" className="py-12 md:py-16 bg-white relative overflow-hidden scroll-mt-24">
      {/* Subtle Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-aarsa-gold-100/10 rounded-full blur-3xl"
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-aarsa-sage-100/10 rounded-full blur-3xl"
          animate={{
            opacity: [0.3, 0.5, 0.3],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Compact Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold font-playfair mb-3">
            Why Choose <span className="gradient-text">Aarsa</span>
          </h2>
          <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Experience the difference with our commitment to quality and service
          </p>
        </motion.div>

        {/* Compact Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto">
          {badges.map((badge, index) => {
            const Icon = badge.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative"
              >
                {/* Compact Card */}
                <div className="bg-white rounded-lg p-5 md:p-6 border border-gray-100 hover:border-aarsa-sage-200 hover:shadow-md transition-all duration-300 h-full">
                  {/* Icon */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-aarsa-sage-50 to-aarsa-gold-50 group-hover:from-aarsa-sage-100 group-hover:to-aarsa-gold-100 transition-colors duration-300 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-aarsa-sage-600 group-hover:text-aarsa-sage-700 transition-colors duration-300" />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 mb-1.5 text-sm md:text-base leading-tight">
                        {badge.title}
                      </h3>
                      <p className="text-xs md:text-sm text-gray-600 leading-relaxed">
                        {badge.description}
                      </p>
                    </div>
                  </div>

                  {/* Subtle Bottom Line */}
                  <motion.div
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.05 + 0.2 }}
                    className="mt-4 h-0.5 bg-gradient-to-r from-transparent via-aarsa-sage-200 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
